import { useState } from 'react';
import type { ReactNode } from 'react';
import type { CoreLocation, CoreOrganization } from '@/lib/zifekCore';
import { createCoreLocation, setCoreLocationStatus, updateCoreLocation } from '@/lib/zifekCore';
import Modal from './Modal';

interface Props {
  org: CoreOrganization;
  locations: CoreLocation[];
  onChanged: () => void;
}

interface FormState {
  id: number | null;
  name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
}

const EMPTY: FormState = {
  id: null, name: '', phone: '', line1: '', line2: '', city: '', region: '', postalCode: '', country: '', isPrimary: false,
};

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-foreground-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function LocationsManager({ org, locations, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const openCreate = () => {
    setForm({ ...EMPTY, isPrimary: locations.length === 0 });
    setError('');
    setOpen(true);
  };

  const openEdit = (l: CoreLocation) => {
    setForm({
      id: l.id,
      name: l.name,
      phone: l.phone || '',
      line1: l.core_addresses?.address_line_1 || '',
      line2: l.core_addresses?.address_line_2 || '',
      city: l.core_addresses?.city || '',
      region: l.core_addresses?.region || '',
      postalCode: l.core_addresses?.postal_code || '',
      country: l.core_addresses?.country || '',
      isPrimary: !!l.is_primary,
    });
    setError('');
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Le nom de l’établissement est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        tenantId: org.tenant_id,
        organizationId: org.id,
        name: form.name.trim(),
        phone: form.phone || null,
        isPrimary: form.isPrimary,
        address: {
          line1: form.line1, line2: form.line2, city: form.city,
          region: form.region, postalCode: form.postalCode, country: form.country,
        },
      };
      if (form.id) await updateCoreLocation(form.id, payload);
      else await createCoreLocation(payload);
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (l: CoreLocation) => {
    setBusyId(l.id);
    try {
      await setCoreLocationStatus(l.id, l.status === 'active' ? 'inactive' : 'active');
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const setPrimary = async (l: CoreLocation) => {
    setBusyId(l.id);
    try {
      await updateCoreLocation(l.id, {
        tenantId: org.tenant_id, organizationId: org.id, name: l.name, phone: l.phone, isPrimary: true,
      });
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Les points de vente de <strong className="text-foreground-700">{org.commercial_name || org.legal_name}</strong>.
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nouvel établissement
        </button>
      </div>

      {locations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-store-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun établissement pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-store-2-line text-lg"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-950 truncate">{l.name}</div>
                    <div className="text-[11px] text-foreground-400">{l.is_primary ? 'Établissement principal' : 'Établissement'}</div>
                  </div>
                </div>
                {l.is_primary && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium whitespace-nowrap">Principal</span>
                )}
              </div>

              <div className="text-xs text-foreground-500 space-y-1 mb-4 flex-1">
                <div className="flex items-start gap-1.5">
                  <i className="ri-map-pin-2-line text-foreground-400 mt-0.5"></i>
                  <span>{[l.core_addresses?.address_line_1, l.core_addresses?.city, l.core_addresses?.country].filter(Boolean).join(', ') || 'Adresse non renseignée'}</span>
                </div>
                <div className="flex items-center gap-1.5"><i className="ri-phone-line text-foreground-400"></i><span>{l.phone || '—'}</span></div>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t border-background-200/60">
                <button
                  onClick={() => openEdit(l)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line"></i> Modifier
                </button>
                {!l.is_primary && l.status === 'active' && (
                  <button
                    onClick={() => setPrimary(l)}
                    disabled={busyId === l.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-star-line"></i> Principal
                  </button>
                )}
                <button
                  onClick={() => toggleStatus(l)}
                  disabled={busyId === l.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 ${
                    l.status === 'active' ? 'bg-background-100 text-foreground-600 hover:bg-background-200/70' : 'bg-accent-100 text-accent-900'
                  }`}
                >
                  <i className={l.status === 'active' ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  {l.status === 'active' ? 'Désactiver' : 'Activer'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier l’établissement' : 'Nouvel établissement'}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
          <div className="space-y-3">
            <Field label="Nom de l’établissement *">
              <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Fès Centre" />
            </Field>
            <Field label="Téléphone">
              <input className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+212 ..." />
            </Field>
            <Field label="Adresse">
              <input className={INPUT} value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Ligne d’adresse" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ville">
                <input className={INPUT} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field label="Région">
                <input className={INPUT} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
              </Field>
              <Field label="Code postal">
                <input className={INPUT} value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
              </Field>
              <Field label="Pays">
                <input className={INPUT} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </Field>
            </div>
            <label className="flex items-center gap-2.5 pt-1 cursor-pointer">
              <input type="checkbox" checked={form.isPrimary} onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
              <span className="text-sm text-foreground-700">Définir comme établissement principal</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}